/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React, { useCallback } from 'react';
import NumberFormat from '../../utils/number-format';
import { ImageFiles } from './ImageFiles';
import Loader from '../loader';
import { ToggleIcon } from './ToggleIcon';
import { EvidenceFile } from '../survey/question/questionInterfaces';
import './EvidenceContainer.scss';
import { HandleFileDescriptionFn } from '../../types/file';

function fileContainer(i: number, file: EvidenceFile, props: EvidenceContainerProps, content: JSX.Element) {
  return (
    <div className='file text-truncate d-flex align-items-center' key={`fileContainer_${i}`}>
      <div className='mr-1'>
        {props.toggleFile ? <ToggleIcon
          i={i}
          file={file}
          allowToggle={props.allowToggle}
          saving={props.saving}
          toggleFile={props.toggleFile}
        /> : <></>}
      </div>
      {content}
    </div>
  );
}

function showEvidenceLink(i: number, file: EvidenceFile, props: EvidenceContainerProps) {
  if ('file' in file) {
    return file.file.name;
  }

  const isDeleted = file.isDeleted ? 'is-deleted' : '';
  const status = file.status ?? '';

  const isValidUrl = (value: string) => {
    const urlRegex = new RegExp(/(https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*))/g);
    const result = value && value.match(urlRegex);
    return result !== null;
  };

  const content = (
    <div className='d-flex align-items-center gap-1'>
      <i className={`utrv-status ${status} fa fa-${file.public ? 'link' : 'lock'} ${isDeleted}`} />
      {isValidUrl(file.link || file.url) ? (
        <a href={file.link || file.url} target={'_blank'} rel='noopener noreferrer'>
          {file.link || file.url}
        </a>
      ) : (
        <>{file.link || file.url}</>
      )}
      {file.description ? (
        <span className='file-description text-ThemeTextMedium'>{file.description}</span>
      ) : null}
    </div>
  );
  return fileContainer(i, file, props, content)
}

const getFileType = (file: EvidenceFile) => {
  const mimetype = 'metadata' in file ? file.metadata.mimetype : file.file.type;

  const res = mimetype.split('/');
  if (Array.isArray(res)) {
    return res[0];
  }
  return mimetype;
};

function fileDownload(url?: string) {
  if (url) {
    window.open(url, '_blank', '');
  }
}

function nonImageFile(i: number, file: EvidenceFile, props: EvidenceContainerProps) {
  if (file.type === 'link') {
    return showEvidenceLink(i, file, props);
  }

  if ('metadata' in file) {
    const { name } = file.metadata;
    const status = file.status ?? '';
    const isDeleted = file.isDeleted ? 'is-deleted' : '';

    const content = <>
      <i className={`fa utrv-status ${status} ${isDeleted} fa-file`} />
      <span style={{ cursor: file.url ? 'pointer' : 'default' }} onClick={() => fileDownload(file.url)}>
        {name} (<NumberFormat value={(file.size) / 1024} decimalPlaces={1} suffix='kb' />)
      </span>
    </>;
    return fileContainer(i, file, props, content)
  }

  const name = file.file.name;

  const content = <>
    <i className={'fa utrv-status fa-file'} />
    {name} (<NumberFormat value={(file.file.size) / 1024} decimalPlaces={1} suffix='kb' />)
  </>;
  return fileContainer(i, file, props, content)
}

interface EvidenceContainerProps {
  files: EvidenceFile[];
  toggleFile?: (i: number, file: EvidenceFile) => void;
  allowToggle: boolean;
  isLoaded: boolean;
  saving: boolean;
  handleFileDescriptionAdd?: HandleFileDescriptionFn;
}

export const EvidenceContainer = (props: EvidenceContainerProps) => {
  const { files, toggleFile, allowToggle = false, isLoaded = true, handleFileDescriptionAdd } = props;

  const toggleWrapper = useCallback((i, file) => {
    const index = files.indexOf(file);
    // Must find file from original array, due to split of images/files arrays
    if (index !== -1 && toggleFile) {
      toggleFile(index, file);
    }

  }, [files, toggleFile])

  if (!isLoaded) {
    return <div className='mt-2' style={{minHeight: '60px', position: 'relative'}}>
      <Loader />
    </div>;
  }

  const other: EvidenceFile[] = [];
  const photos: EvidenceFile[] = [];

  files.forEach(file => {
    if (file.type !== 'link' && getFileType(file) === 'image') {
      photos.push(file);
    } else {
      other.push(file);
    }
  })

  const nonImageProps = {
    ...props,
    toggleFile: toggleWrapper,
  };

  return (
    <div className='text-left existing-evidence-container mb-3'>
      <ImageFiles
        handleFileDescriptionAdd={handleFileDescriptionAdd}
        photos={photos}
        allowToggle={allowToggle}
        toggleFile={toggleFile ? toggleWrapper : undefined}
      />
      {other.map((o, i) => nonImageFile(i, o, nonImageProps))}
    </div>
  );
}
